/* ========================================================================= *
 * BowlOrders - the bowl board's work, ON THE REP WORKLIST, as work orders.
 *
 * Brian, 2026-10-26: "remember this all must be fully integrated to worklist -
 * to each element of the build." So every element of the bowl row lands here
 * for the right person: an exec sees "Pick who goes after Georgia" and
 * "Confirm Memphis's school account"; the desk (lead + pair partner, so Zach
 * AND Alexis, Philip AND Madison) sees "Email UNT about the bowl ring", then
 * Art, Quote, Win, and "Add a contact" when there is nobody to write to.
 *
 * ⭐ SAME SHAPE AS TradeOrders, NOT A SIDE LIST. An ORDER and a REASON
 * (the-work-order-pattern), on /my-worklist, following the person-switcher.
 * The orders come from the server's buildOrders(), built from the same
 * teamWork() the board row draws, and each CLEARS by disappearing once the
 * ledger or the desk says it is done. Nothing is ticked off here.
 *
 * ⚠️ IT RECONCILES ON ITS FACE: orders for this person + orders with somebody
 * else = every order on the board. A read failure is said in words, never
 * rendered as "no bowl work".
 * ========================================================================= */
import { useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useFencedState as useState } from '../../lib/bowlMutationContext.jsx'
import { api } from '../../lib/api.js'
import { orderHref, reconcileLine } from '../../lib/bowlWork.js'
import { useAccess } from '../../lib/access.jsx'
import { workOrdersReadState, READ_FAILURE_COPY } from '../../lib/assignmentSetup.js'
import ReadinessNotice from './ReadinessNotice.jsx'
import actionStyles from './bowlActions.module.css'
import styles from '../trades/Trades.module.css'

export default function BowlOrders({ who = '' }) {
  /* Whether to offer the setup fix. UX only: the server decides the write. */
  const { ctx: accessCtx } = useAccess()
  const viewerIsExec = accessCtx?.role === 'exec'
  const [state, setState] = useState({ status: 'loading', data: null, error: '' })

  const pendingRead = useRef(null)
  const load = useCallback(() => {
    pendingRead.current?.abort()
    const ctl = new AbortController()
    pendingRead.current = ctl
    const signal = ctl.signal
    setState({ scope: who, status: 'loading', data: null, error: '' })
    api.fbsBowlWorkOrders({ who, signal })
      .then((d) => { if (!signal.aborted) setState({ scope: who, status: 'ready', data: d, error: '' }) })
      .catch((e) => {
        if (signal.aborted) return
        /* Post-V4.001: ONLY the clean 409 refusal (confirmed missing roster) is
         * SETUP. A timeout or other read failure (503 readFailure) is a
         * retryable read, said as one (Dot's review of 390d574). */
        const kind = workOrdersReadState(e)
        if (kind === 'setup') { setState({ scope: who, status: 'setup', data: null, error: '' }); return }
        if (kind === 'read_failure') { setState({ scope: who, status: 'read_failure', data: null, error: READ_FAILURE_COPY.worklist }); return }
        setState({ scope: who, status: 'error', data: null,
          error: e?.body?.detail || e?.body?.error || e?.message || 'The bowl work could not be read.' })
      })
  }, [who, setState])

  useEffect(() => {
    load()
    return () => pendingRead.current?.abort()
  }, [load])

  const whose = !who || who === '*' ? (who === '*' ? 'the team' : 'you') : who
  const current = state.scope === who ? state : {status:'loading',data:null,error:''}
  const d = current.data

  if (!d && current.status === 'setup') {
    return (
      <section className={styles.section} aria-label="Bowl work" data-readiness-state="setup">
        <p className={styles.head}>Bowl eligibility</p>
        <ReadinessNotice readiness={{ state: 'setup' }} isExec={viewerIsExec}
          classes={{ setup: actionStyles.setup, setupText: actionStyles.setupText, btn: `${actionStyles.btn} ${actionStyles.btnFix}` }} />
      </section>
    )
  }

  if (!d && current.status === 'read_failure') {
    return (
      <section className={styles.section} data-tone="bad" aria-label="Bowl work" data-readiness-state="read_failure">
        <p className={styles.head}>Bowl eligibility</p>
        <p className={styles.sentence} role="status">{current.error}</p>
        <div className={styles.controls}>
          <button type="button" className={styles.cta} data-primary="1" onClick={() => load()}>Read the bowl work again</button>
        </div>
      </section>
    )
  }

  if (!d) {
    return (
      <section className={styles.section} data-tone={current.status === 'error' ? 'bad' : undefined} aria-label="Bowl work">
        <p className={styles.head}>Bowl eligibility</p>
        <p className={styles.sentence}>
          {current.status === 'error'
            ? <>The bowl work could not be read: {current.error} This is a refusal, not &ldquo;no bowl work&rdquo;.</>
            : 'Reading the bowl board and addressing its work…'}
        </p>
        {current.status === 'error' ? (
          <div className={styles.controls}>
            <button type="button" className={styles.cta} data-primary="1" onClick={() => load()}>Read the bowl work again</button>
          </div>
        ) : null}
      </section>
    )
  }

  const orders = d.orders || []
  return (
    <section className={styles.section} data-tone={d.reconciles === false ? 'bad' : undefined} aria-label="Bowl work">
      <p className={styles.head}>
        Bowl eligibility: {orders.length} {orders.length === 1 ? 'order' : 'orders'} for {whose}
      </p>
      <p className={styles.recon}>{reconcileLine(d, whose)}</p>
      {d.reconciles === false ? <p className={styles.fail} role="alert">These do not add up. Do not trust this list; reload.</p> : null}
      {d.note ? <p className={styles.sentence}>{d.note}</p> : null}
      {orders.length === 0 ? (
        <p className={styles.sentence}>
          Nothing on the bowl board is waiting on {whose}. The read succeeded and the answer is none.
        </p>
      ) : (
        <ul className={styles.list}>
          {orders.map((o) => (
            <li key={o.id} className={styles.order}>
              <p className={styles.verb}>{o.verb}</p>
              <p className={styles.reason}>{o.reason}</p>
              <div className={styles.controls}>
                <Link className={styles.cta} data-primary="1" to={orderHref(o)}>{o.cta?.label || 'Open'}</Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
